import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import axios from "../axios";
import axiosWithoutInterceptor from "../axiosWithoutInterceptor";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { RootState } from "./types";

// Types

// Per-jurisdiction and job-phase-task rows of a certificate; populated only on the detail fetch.
export interface EcmsCertJurisdiction {
    region: string;
    idNo: string | null;
    idType: string | null;
    endDate: string;
}

export interface EcmsCertJob {
    jobCode: string | null;
    phaseCode: string | null;
    taskCode: string | null;
}

export interface EcmsRecord {
    id: number;
    companyId: number;
    customerName: string;
    customerCode: string;
    avaCertId: string;
    exemptCertId: string;
    country: string;
    region: string;
    exemptReasonId: string;
    businessTypeId: string;
    exemptCertStatusId: string;
    // Detail fields returned by the ECMS list API and surfaced in the customer-detail dialog.
    // Optional because the grid only relies on the core columns above.
    exemptCertTypeId?: string;
    exemptCertReviewStatusId?: string;
    documentRefNo?: string;
    expiryDate?: string;
    // Certificate effective date. ECMS sends it as `effDate`; `effectiveDate` is a legacy alias
    // the ECMS REST layer leaves unset (0001-01-01), so prefer effDate.
    effDate?: string;
    effectiveDate?: string;
    countryIssued?: string;
    regionsApplicable?: string;
    businessTypeDescription?: string;
    exemptReasonDescription?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    postalCode?: string;
    // Nested detail arrays — only present on the detail fetch; null on the grid list call.
    exemptCertDetails?: EcmsCertJurisdiction[] | null;
    exemptCertJobDetails?: EcmsCertJob[] | null;
}

export interface PaginateState {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
    indeterminate: boolean;
}

export interface PageState {
    data: EcmsRecord[];
    paginate: PaginateState;
}

export interface EcmsFiltersState {
    companyId: string;
    customerName: string;
    customerCode: string;
    avaCertId: string;
    exemptCertId: string;
    country: string[];
    region: string[];
}

/** Full-detail fetch state for the certificate-detail dialog; kept separate from the grid page. */
export interface EcmsCertDetailState {
    record: EcmsRecord | null;
    isLoading: boolean;
    error: string | null;
}

export interface EcmsState {
    isLoading: boolean;
    page: PageState;
    sortColumn: [string, boolean];
    filtersState: EcmsFiltersState;
    hasFilterChips: boolean;
    error: string | null;
    certDetail: EcmsCertDetailState;
}

interface RevokeEcmsCertParams {
    ecmsCertId: number | string;
    auditMessage: string;
}

interface FetchEcmsCertDetailParams {
    exemptCertId: number | string;
    /** Company scope for the ECMS path; falls back to the filters-state company when omitted. */
    companyId?: number | string;
}

interface FetchEcmsParams {
    filterString?: string;
    top?: number;
    skip?: number;
    paginateDetails?: {
        pageNumber: number;
        startIndex: number;
        endIndex: number;
        rowsPerPage: number;
    };
}

interface FetchEcmsResponse {
    data: EcmsRecord[];
    paginate: PaginateState;
}

interface EcmsApiResponse {
    value: EcmsRecord[] | null;
    // The ECMS route returns AvaTax's page shape, whose record count is a string
    // (`@recordsetCount` -> `count`) and is absent when the service doesn't supply one.
    count: string | number | null;
}

export const initialFiltersState: EcmsFiltersState = {
    companyId: "",
    customerName: "",
    customerCode: "",
    avaCertId: "",
    exemptCertId: "",
    country: [],
    region: [],
};

export const initialState: EcmsState = {
    isLoading: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 10,
            totalRecords: 0,
            indeterminate: false,
        },
    },
    // Default sort mirrors the backend fallback (exemptCertId DESC = "Ecms Cert ID").
    sortColumn: ["exemptCertId", false],
    filtersState: initialFiltersState,
    hasFilterChips: false,
    error: null,
    certDetail: {
        record: null,
        isLoading: false,
        error: null,
    },
};

/**
 * Build filter string from ECMS filters state
 * @param filters - ECMS filters state
 * @returns OData filter string
 */
/**
 * Escape single quotes in OData filter values
 * OData requires single quotes to be escaped as two single quotes
 */
const escapeODataString = (value: string): string => {
    return value.replace(/'/g, "''");
};

/**
 * OData $filter for ECMS list. Company scope is only via URL (`companies/{companyId}/ecms`), never here.
 */
const buildEcmsFilterString = (filters: EcmsFiltersState): string => {
    const filterParts: string[] = [];

    /**
     * AvaTax API has LIMITED OData support - only basic operators:
     * - eq, ne, gt, lt, ge, le (comparison)
     * - AND, OR (logical)
     *
     * NO support for: contains, tolower, startswith, etc.
     *
     * For text fields (customerName, customerCode, avaCertId), we cannot filter server-side.
     * Only exact-match filters are supported.
     */

    // Text search fields - use exact match only
    // Note: This is a limitation of AvaTax API - no partial text search support
    if (filters.customerName) {
        const escaped = escapeODataString(filters.customerName);
        filterParts.push(`customerName eq '${escaped}'`);
    }
    if (filters.customerCode) {
        const escaped = escapeODataString(filters.customerCode);
        filterParts.push(`customerCode eq '${escaped}'`);
    }
    if (filters.avaCertId) {
        const escaped = escapeODataString(filters.avaCertId);
        filterParts.push(`avaCertId eq '${escaped}'`);
    }
    if (filters.exemptCertId) {
        // exemptCertId is numeric
        filterParts.push(`exemptCertId eq ${filters.exemptCertId}`);
    }

    // Array filters - use 'or' to combine multiple values
    if (filters.country && filters.country.length > 0) {
        const countryFilters = filters.country
            .map(c => `country eq '${escapeODataString(c)}'`)
            .join(" or ");
        filterParts.push(`(${countryFilters})`);
    }

    // Note: Region filter is disabled in the UI due to backend API bug
    // The AvaTax ECMS API returns 500 error: "The request message was already sent. Cannot send the same request message multiple times."
    // This is caused by HttpRequestMessage reuse in the webapi RestRequest implementation
    // Keep this code for when the backend issue is resolved
    if (filters.region && filters.region.length > 0) {
        const regionFilters = filters.region
            .map(r => `regionsApplicable contains '${escapeODataString(r)}'`)
            .join(" or ");
        filterParts.push(`(${regionFilters})`);
    }

    return filterParts.length > 0 ? filterParts.join(" and ") : "";
};

/**
 * Resolve the record count for the pagination control.
 *
 * The ECMS route hands back AvaTax's page shape, and the ECMS service leaves its
 * `@recordsetCount` unset, so `count` arrives as null. Reporting 0 there hides the pagination
 * control entirely (SPagination is only rendered above 0 records, and treats 0 as "no records"
 * and disables itself), leaving no way to reach page 2.
 *
 * With no total available, page indeterminately instead: assume one more page exists whenever a
 * full page came back, which enables Next and leaves it disabled on a short (final) page. The
 * estimate is replaced by each new response, so it self-corrects as the user pages.
 *
 * @param count - `count` from the response; a string, a number, or null
 * @param rowsReturned - Records in the current page
 * @param pageStart - Index the current page starts at (`$skip`)
 * @param pageSize - Records requested for the page (`$top`)
 * @returns The count to report and whether it is an estimate
 */
export const getEcmsRecordCount = (
    count: string | number | null | undefined,
    rowsReturned: number,
    pageStart: number,
    pageSize: number
): { totalRecords: number; indeterminate: boolean } => {
    const reportedTotal = Number(count) || 0;
    if (reportedTotal > 0) {
        return { totalRecords: reportedTotal, indeterminate: false };
    }
    if (rowsReturned === 0) {
        return { totalRecords: 0, indeterminate: false };
    }
    const seenSoFar = pageStart + rowsReturned;
    const hasFullPage = rowsReturned >= pageSize;
    return { totalRecords: hasFullPage ? seenSoFar + 1 : seenSoFar, indeterminate: true };
};

/**
 * Build the ECMS list URL: company scope goes in the path, everything else in the query.
 *
 * @param params - Fetch params supplying an explicit filter string and `$skip`
 * @param ecms - Slice state holding the sort column and filters
 * @param pageSize - Records to request (`$top`)
 */
const buildEcmsRequestUrl = (
    { filterString, skip }: FetchEcmsParams,
    ecms: EcmsState,
    pageSize: number
): string => {
    const [sortField, ascending] = ecms?.sortColumn || initialState.sortColumn;
    const companyId = ecms.filtersState.companyId?.trim();
    const filter =
        filterString !== undefined ? filterString : buildEcmsFilterString(ecms.filtersState);

    return buildApiV3UrlWithQuery(
        companyId ? `companies/${companyId}/ecms` : "ecms",
        filter || null,
        null,
        pageSize,
        skip,
        `${sortField} ${ascending ? "ASC" : "DESC"}`
    );
};

/**
 * Pagination state for a fetched page. Callers that page supply `paginateDetails`; the rest don't,
 * so derive the page from `$skip` — which is 0 for every such caller today, giving page-one
 * defaults, but keeps the reported page and the record count describing the same rows either way.
 *
 * @param params - Fetch params supplying the caller's paginate details and `$skip`
 * @param pageSize - Records requested for the page (`$top`)
 * @param count - `count` from the response
 * @param rows - Records the response returned
 */
const buildEcmsPaginateState = (
    { paginateDetails, skip }: FetchEcmsParams,
    pageSize: number,
    count: string | number | null,
    rows: EcmsRecord[] | null
): PaginateState => {
    const pageStart = paginateDetails?.startIndex ?? skip ?? 0;
    const page = paginateDetails ?? {
        pageNumber: Math.floor(pageStart / pageSize) + 1,
        startIndex: pageStart,
        endIndex: pageStart + pageSize - 1,
        rowsPerPage: pageSize,
    };
    const { totalRecords, indeterminate } = getEcmsRecordCount(
        count,
        rows?.length ?? 0,
        pageStart,
        pageSize
    );

    return { ...page, totalRecords, indeterminate };
};

/**
 * Async thunk for fetching ECMS records with pagination, sorting, and filtering
 * @param params - Fetch parameters including filter, pagination details
 * @returns Promise with ECMS data and pagination info
 */
export const fetchEcms = createAsyncThunk<
    FetchEcmsResponse,
    FetchEcmsParams | undefined,
    { rejectValue: string }
    // eslint-disable-next-line default-param-last
>("ecms/fetchEcms", async (params = {}, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const { session, ecms } = state;
        const finalPageSize = params.top || session?.rowsPerPage;

        const apiURI = buildEcmsRequestUrl(params, ecms, finalPageSize);

        const response: AxiosResponse<EcmsApiResponse> = await axios.get(apiURI, {
            withCredentials: true,
        });

        if (response?.status === 200 && response?.data?.value !== null) {
            return {
                data: response.data.value,
                paginate: buildEcmsPaginateState(
                    params,
                    finalPageSize,
                    response.data.count,
                    response.data.value
                ),
            };
        }
        return thunkAPI.rejectWithValue("No data found");
    } catch (error: unknown) {
        if (error instanceof Error) {
            return thunkAPI.rejectWithValue(error.message);
        }
        return thunkAPI.rejectWithValue("An unknown error occurred");
    }
});

/**
 * Async thunk for revoking an ECMS certificate
 * @param params - ecmsCertId and auditMessage for the revoke request
 * @returns Promise that resolves on success or rejects with error message
 */
export const revokeEcmsCert = createAsyncThunk<void, RevokeEcmsCertParams, { rejectValue: string }>(
    "ecms/revokeEcmsCert",
    async ({ ecmsCertId, auditMessage }, thunkAPI) => {
        try {
            const url = buildApiV3Url(`${ecmsCertId}/revoke`);
            await axiosWithoutInterceptor.patch(
                url,
                { AuditMessage: auditMessage },
                { withCredentials: true }
            );
            return;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue("An unknown error occurred");
        }
    }
);

/**
 * Async thunk for fetching a single ECMS certificate's full details, including the nested
 * per-jurisdiction (`exemptCertDetails`) and job (`exemptCertJobDetails`) arrays that the list
 * call omits. Reuses the list endpoint filtered to one cert with `$include`, so no new backend
 * route is required.
 * @param params - exemptCertId to fetch and the company scope for the path
 * @returns Promise resolving to the single {@link EcmsRecord} with nested arrays populated
 */
export const fetchEcmsCertificateDetail = createAsyncThunk<
    EcmsRecord,
    FetchEcmsCertDetailParams,
    { rejectValue: string }
>("ecms/fetchEcmsCertificateDetail", async ({ exemptCertId, companyId }, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const scopedCompanyId = String(companyId ?? state.ecms.filtersState.companyId ?? "").trim();
        const ecmsRelativePath = scopedCompanyId ? `companies/${scopedCompanyId}/ecms` : "ecms";

        // exemptCertId is numeric in OData, so it is not quoted.
        const filterString = `exemptCertId eq ${exemptCertId}`;

        // "details" is the $include token the downstream ECMS REST service expects to populate
        // both the exemptCertDetails and exemptCertJobDetails arrays (see AvaTaxEcmsModel).
        const apiURI = buildApiV3UrlWithQuery(
            ecmsRelativePath,
            filterString,
            ["details"],
            1,
            0,
            null
        );

        const response: AxiosResponse<EcmsApiResponse> = await axios.get(apiURI, {
            withCredentials: true,
        });

        const record = response?.data?.value?.[0];
        if (response?.status === 200 && record) {
            return record;
        }
        return thunkAPI.rejectWithValue("Certificate details not found");
    } catch (error: unknown) {
        if (error instanceof Error) {
            return thunkAPI.rejectWithValue(error.message);
        }
        return thunkAPI.rejectWithValue("An unknown error occurred");
    }
});

const ecmsSlice = createSlice({
    name: "ecms",
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<PageState>) => {
            state.page.data = action.payload.data;
            state.page.paginate = action.payload.paginate;
        },
        setPageToNull: state => {
            state.page = initialState.page;
        },
        setSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
        setEcmsFiltersState: (state, action: PayloadAction<Partial<EcmsFiltersState>>) => {
            state.filtersState = { ...state.filtersState, ...action.payload };
        },
        clearEcmsFiltersState: state => {
            state.filtersState = initialFiltersState;
        },
        setHasFilterChips: (state, action: PayloadAction<boolean>) => {
            state.hasFilterChips = action.payload;
        },
        clearCertDetail: state => {
            state.certDetail = initialState.certDetail;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchEcms.pending, state => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(fetchEcms.fulfilled, (state, action) => {
            state.isLoading = false;
            state.error = null;
            if (action.payload?.data) {
                state.page.data = action.payload.data;
                state.page.paginate = action.payload.paginate;
            } else {
                state.page = initialState.page;
            }
        });
        builder.addCase(fetchEcms.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload || "Failed to fetch ECMS data";
            // Clear the grid data on error
            state.page = initialState.page;
        });
        builder.addCase(revokeEcmsCert.fulfilled, state => {
            state.error = null;
        });
        builder.addCase(revokeEcmsCert.rejected, (state, action) => {
            state.error = action.payload || "Failed to revoke ECMS certificate";
        });
        // Assign the whole certDetail object (rather than mutating nested props) so the reducer
        // stays safe even when a preloaded state omits certDetail.
        builder.addCase(fetchEcmsCertificateDetail.pending, state => {
            state.certDetail = { record: null, isLoading: true, error: null };
        });
        builder.addCase(fetchEcmsCertificateDetail.fulfilled, (state, action) => {
            state.certDetail = { record: action.payload, isLoading: false, error: null };
        });
        builder.addCase(fetchEcmsCertificateDetail.rejected, (state, action) => {
            state.certDetail = {
                record: null,
                isLoading: false,
                error: action.payload || "Failed to fetch certificate details",
            };
        });
    },
});

export const {
    setPage,
    setSortColumn,
    setPageToNull,
    setEcmsFiltersState,
    clearEcmsFiltersState,
    setHasFilterChips,
    clearCertDetail,
} = ecmsSlice.actions;

// Selectors
export const selectEcms = (state: RootState): PageState => state.ecms.page;

export const selectEcmsIsLoading = (state: RootState): boolean => state.ecms.isLoading;

export const selectSortColumn = (state: RootState): [string, boolean] => state.ecms.sortColumn;

export const selectEcmsFiltersState = (state: RootState): EcmsFiltersState =>
    state.ecms.filtersState;

export const selectHasFilterChips = (state: RootState): boolean => state.ecms.hasFilterChips;

export const selectEcmsError = (state: RootState): string | null => state.ecms.error;

// Stable fallback so components stay safe when a test preloads an ecms slice without certDetail
// (RTK preloadedState replaces the slice rather than deep-merging with initialState).
const EMPTY_CERT_DETAIL: EcmsCertDetailState = { record: null, isLoading: false, error: null };

export const selectEcmsCertDetail = (state: RootState): EcmsCertDetailState =>
    state.ecms.certDetail ?? EMPTY_CERT_DETAIL;

export default ecmsSlice.reducer;
